import { v4 as uuidv4 } from 'uuid';

// Define shared types
export type Vector3 = [number, number, number];
export type NPCType = 'genin' | 'chicken';
export type NPCState = {
  id: string;
  position: Vector3;
  facingDirection: Vector3;
  health: number;
  npcType: NPCType;
  behaviorState: 'patrolling' | 'fleeing' | 'dead';
  patrolPath: Vector3[]; // Array of points to patrol between
  currentPatrolIndex: number; // Current target point in the path
  lastUpdate: number;
  respawnTime?: number; // When to respawn if dead
  patrolRadius?: number; // For animals that wander randomly
};

// Define ground level constant (matching server)
const GROUND_LEVEL = 0.7;

// Define world boundaries for NPC spawning
const WORLD = {
  MIN_X: -100,
  MAX_X: 100,
  MIN_Z: -100,
  MAX_Z: 100,
  // Special zones where NPCs are more likely to spawn
  ZONES: [
    // Village center
    { x: 0, z: 0, radius: 30, density: 0.3 },
    // Market area
    { x: 20, z: 15, radius: 20, density: 0.2 },
    // Training grounds
    { x: -30, z: 40, radius: 25, density: 0.15 },
    // Farm areas (chickens preferred)
    { x: 60, z: -20, radius: 35, density: 0.2 },
    { x: -50, z: -40, radius: 25, density: 0.15 },
  ],
};

// Generate a random point within the world boundaries
const getRandomWorldPosition = (): Vector3 => {
  // 70% chance to spawn in a special zone
  if (Math.random() < 0.7) {
    // Pick a random zone
    const zone = WORLD.ZONES[Math.floor(Math.random() * WORLD.ZONES.length)];

    // Get random distance from center (biased toward center with sqrt)
    const distance = Math.sqrt(Math.random()) * zone.radius;
    const angle = Math.random() * Math.PI * 2;

    return [
      zone.x + Math.cos(angle) * distance,
      GROUND_LEVEL,
      zone.z + Math.sin(angle) * distance,
    ];
  }

  // 30% chance to spawn anywhere in the world
  return [
    WORLD.MIN_X + Math.random() * (WORLD.MAX_X - WORLD.MIN_X),
    GROUND_LEVEL,
    WORLD.MIN_Z + Math.random() * (WORLD.MAX_Z - WORLD.MIN_Z),
  ];
};

// Generate a random patrol path with specified number of points
const generateRandomPatrolPath = (
  pointCount: number = 4,
  maxDistance: number = 15
): Vector3[] => {
  // Start with a random position in the world
  const startPosition = getRandomWorldPosition();
  const path: Vector3[] = [startPosition];

  // Generate additional points around the start position
  for (let i = 1; i < pointCount; i++) {
    // Random angle and distance
    const angle = Math.random() * Math.PI * 2;
    const distance = 3 + Math.random() * maxDistance; // At least 3 units away

    path.push([
      startPosition[0] + Math.cos(angle) * distance,
      GROUND_LEVEL,
      startPosition[2] + Math.sin(angle) * distance,
    ]);
  }

  return path;
};

// Generate a patrol path specifically for chickens (smaller area)
const generateChickenPath = (): { path: Vector3[]; radius: number } => {
  const startPosition = getRandomWorldPosition();
  const pointCount = 3 + Math.floor(Math.random() * 3); // 3-5 points
  const path: Vector3[] = [startPosition];
  const radius = 2 + Math.random() * 4; // 2-6 units radius

  for (let i = 1; i < pointCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const distance = Math.random() * radius;

    path.push([
      startPosition[0] + Math.cos(angle) * distance,
      GROUND_LEVEL,
      startPosition[2] + Math.sin(angle) * distance,
    ]);
  }

  return { path, radius };
};

// Create NPCs with initial states (count parameter allows for scaling)
export const createInitialNPCs = (count: number = 10): NPCState[] => {
  const npcs: NPCState[] = [];

  // Calculate the number of each type
  // More genin than chickens (70/30 split)
  const geninCount = Math.floor(count * 0.7);
  const chickenCount = count - geninCount;

  // Create genin NPCs
  for (let i = 0; i < geninCount; i++) {
    const path = generateRandomPatrolPath(
      4 + Math.floor(Math.random() * 4),
      20
    ); // 4-8 points

    // Create a genin NPC
    npcs.push({
      id: uuidv4(),
      position: [...path[0]], // Start at first point of patrol path
      facingDirection: [1, 0, 0],
      health: 100,
      npcType: 'genin',
      behaviorState: 'patrolling',
      patrolPath: path,
      currentPatrolIndex: 1, // Target the second point first
      lastUpdate: Date.now(),
    });
  }

  // Create chicken NPCs
  for (let i = 0; i < chickenCount; i++) {
    const { path, radius } = generateChickenPath();

    // Create a chicken NPC
    npcs.push({
      id: uuidv4(),
      position: [...path[0]], // Start at first point of patrol path
      facingDirection: [1, 0, 0],
      health: 30, // Chickens have less health
      npcType: 'chicken',
      behaviorState: 'patrolling',
      patrolPath: path,
      currentPatrolIndex: 1, // Target the second point first
      lastUpdate: Date.now(),
      patrolRadius: radius, // Chickens can wander within this radius
    });
  }

  return npcs;
};

// Update NPC positions based on their behavior
export const updateNPCPositions = (
  npcs: NPCState[],
  deltaTime: number
): NPCState[] => {
  return npcs.map((npc) => {
    // Skip dead NPCs
    if (npc.behaviorState === 'dead') {
      // Check if it's time to respawn
      if (npc.respawnTime && Date.now() > npc.respawnTime) {
        if (npc.npcType === 'chicken') {
          // Generate a new patrol path for chicken
          const { path, radius } = generateChickenPath();
          return {
            ...npc,
            health: 30, // Chickens have less health
            behaviorState: 'patrolling',
            respawnTime: undefined,
            position: path[0],
            patrolPath: path,
            patrolRadius: radius,
            currentPatrolIndex: 1,
          };
        } else {
          // Generate a new random patrol path for genin
          const path = generateRandomPatrolPath(
            4 + Math.floor(Math.random() * 3),
            20
          );
          return {
            ...npc,
            health: 100,
            behaviorState: 'patrolling',
            respawnTime: undefined,
            position: path[0],
            patrolPath: path,
            currentPatrolIndex: 1,
          };
        }
      }
      return npc;
    }

    // Handle patrolling behavior
    if (npc.behaviorState === 'patrolling') {
      const targetPoint = npc.patrolPath[npc.currentPatrolIndex];
      const [currentX, currentY, currentZ] = npc.position;
      const [targetX, targetY, targetZ] = targetPoint;

      // Calculate direction to target
      const dx = targetX - currentX;
      const dz = targetZ - currentZ;

      // Calculate distance to target
      const distanceToTarget = Math.sqrt(dx * dx + dz * dz);

      // Check if we've reached the target point
      if (distanceToTarget < 0.5) {
        // Special behavior for chickens
        if (npc.npcType === 'chicken') {
          // Chickens have a chance to pause and peck at the ground
          const shouldPause = Math.random() < 0.3;

          if (shouldPause) {
            return {
              ...npc,
              lastUpdate: Date.now() + Math.random() * 2000, // Delay next movement
            };
          }

          // Chickens occasionally generate a random path change
          const shouldWander = Math.random() < 0.2;

          if (shouldWander && npc.patrolRadius) {
            // Find the center of the current path (approximate)
            const sumX = npc.patrolPath.reduce((sum, pt) => sum + pt[0], 0);
            const sumZ = npc.patrolPath.reduce((sum, pt) => sum + pt[2], 0);
            const centerX = sumX / npc.patrolPath.length;
            const centerZ = sumZ / npc.patrolPath.length;

            // Generate a new random point
            const angle = Math.random() * Math.PI * 2;
            const distance = Math.random() * npc.patrolRadius;
            const newPoint: Vector3 = [
              centerX + Math.cos(angle) * distance,
              GROUND_LEVEL,
              centerZ + Math.sin(angle) * distance,
            ];

            // Update the patrol path by replacing the next point
            const newPatrolPath = [...npc.patrolPath];
            const newIndex =
              (npc.currentPatrolIndex + 1) % newPatrolPath.length;
            newPatrolPath[newIndex] = newPoint;

            return {
              ...npc,
              patrolPath: newPatrolPath,
              currentPatrolIndex:
                (npc.currentPatrolIndex + 1) % npc.patrolPath.length,
              lastUpdate: Date.now(),
            };
          }
        }

        // Move to next point in path
        return {
          ...npc,
          currentPatrolIndex:
            (npc.currentPatrolIndex + 1) % npc.patrolPath.length,
          lastUpdate: Date.now(),
        };
      }

      // Calculate normalized direction
      const length = Math.sqrt(dx * dx + dz * dz);
      const normalizedDx = dx / length;
      const normalizedDz = dz / length;

      // Calculate speed based on NPC type
      const speed = npc.npcType === 'chicken' ? 0.8 : 1.2; // Chickens move slower

      // Calculate new position
      const newX = currentX + normalizedDx * speed * deltaTime;
      const newZ = currentZ + normalizedDz * speed * deltaTime;

      return {
        ...npc,
        position: [newX, currentY, newZ],
        facingDirection: [normalizedDx, 0, normalizedDz],
        lastUpdate: Date.now(),
      };
    }

    // Handle fleeing behavior
    if (npc.behaviorState === 'fleeing') {
      // Adjust fleeing speed based on NPC type
      const speed = npc.npcType === 'chicken' ? 3.0 : 2.5; // Chickens flee faster

      const [currentX, currentY, currentZ] = npc.position;
      const [dirX, dirY, dirZ] = npc.facingDirection;

      const newX = currentX + dirX * speed * deltaTime;
      const newZ = currentZ + dirZ * speed * deltaTime;

      // Chickens have a small chance to calm down each update
      if (npc.npcType === 'chicken' && Math.random() < 0.01) {
        return {
          ...npc,
          position: [newX, currentY, newZ],
          behaviorState: 'patrolling',
          lastUpdate: Date.now(),
        };
      }

      return {
        ...npc,
        position: [newX, currentY, newZ],
        lastUpdate: Date.now(),
      };
    }

    return npc;
  });
};

// Calculate hit for an NPC
export const handleNPCHit = (
  npcs: NPCState[],
  npcId: string,
  damage: number,
  position: Vector3
): NPCState[] => {
  return npcs.map((npc) => {
    if (npc.id !== npcId) return npc;

    // Adjust damage based on NPC type
    let adjustedDamage = damage;
    if (npc.npcType === 'chicken') {
      adjustedDamage = damage * 1.5; // Chickens take more damage
    }

    const newHealth = Math.max(0, npc.health - adjustedDamage);

    if (newHealth <= 0) {
      // NPC is dead
      return {
        ...npc,
        health: 0,
        behaviorState: 'dead',
        // Chickens respawn faster than genin
        respawnTime: Date.now() + (npc.npcType === 'chicken' ? 5000 : 10000),
      };
    }

    // Different fleeing thresholds for different NPC types
    const fleeingThreshold = npc.npcType === 'chicken' ? 15 : 30;

    if (newHealth < fleeingThreshold) {
      // Calculate direction away from hit source
      const [hitX, hitY, hitZ] = position;
      const [npcX, npcY, npcZ] = npc.position;

      // Calculate direction vector away from hit
      const dx = npcX - hitX;
      const dz = npcZ - hitZ;

      // Normalize
      const length = Math.sqrt(dx * dx + dz * dz);
      const fleeDirection: Vector3 = [dx / length, 0, dz / length];

      // NPC is scared and fleeing
      return {
        ...npc,
        health: newHealth,
        behaviorState: 'fleeing',
        facingDirection: fleeDirection,
      };
    }

    // Just take damage
    return {
      ...npc,
      health: newHealth,
    };
  });
};
